import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createFeedback } from "@/data-access/feedback";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { LoaderCircle, MessageSquare, CheckCircle, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const feedbackSchema = z.object({
  message: z.string().min(1, "Feedback message is required").max(5000, "Feedback message is too long (maximum 5000 characters)"),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

export default function FeedbackPage() {
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      message: "",
    },
  });

  const createFeedbackMutation = useMutation({
    mutationFn: createFeedback,
    onSuccess: (data) => {
      setSubmitted(true);
      form.reset();
    },
    onError: (error: Error) => {
      console.error("Error submitting feedback:", error);
    },
  });

  const onSubmit = (data: FeedbackFormData) => {
    createFeedbackMutation.mutate(data);
  };

  const handleSubmitAnother = () => {
    setSubmitted(false);
    createFeedbackMutation.reset();
  };

  const isLoading = createFeedbackMutation.isPending;
  const error = createFeedbackMutation.error;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="max-w-screen-md mx-auto w-full">
        <div className="flex-grow pt-2 pb-2 px-0 sm:px-2 lg:px-4">
          <div className="space-y-4">
            <div className="mt-3 px-4">
              <div className="mb-6 flex items-center gap-3">
                <MessageSquare className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold text-foreground">Feedback</h1>
              </div>
              <p className="text-muted-foreground mb-6">
                We'd love to hear from you! Share your thoughts, suggestions, or report any issues you've encountered.
              </p>
            </div>

            <div className="px-4">
              {submitted ? (
                <Card className="border border-border">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 text-success mb-4">
                      <CheckCircle className="h-8 w-8" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Thank you for your feedback!</h3>
                    <p className="text-muted-foreground mb-6 text-center">
                      Our team will review it shortly. Your input helps us make Blockseer Explorer better.
                    </p>
                    <Button 
                      onClick={handleSubmitAnother}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Submit Another Feedback
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border border-border">
                  <CardHeader>
                    <CardTitle className="text-lg text-foreground">Share Your Feedback</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {error && (
                      <Alert className="mb-6 border-destructive/50 bg-destructive/10">
                        <AlertCircle className="h-4 w-4 text-destructive" />
                        <AlertDescription className="text-destructive">
                          {error.message || "Failed to submit feedback. Please try again."}
                        </AlertDescription>
                      </Alert>
                    )}

                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        <FormField
                          control={form.control}
                          name="message"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-foreground">
                                Your Feedback *
                              </FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Tell us what you think, what features you'd like to see, or any issues you've encountered..."
                                  className="min-h-[150px] resize-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:transition-none bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground transition-all duration-200"
                                  {...field}
                                />
                              </FormControl>
                              <div className="flex justify-between items-center">
                                <FormMessage className="text-destructive text-sm" />
                                <span className="text-xs text-muted-foreground">
                                  {field.value.length}/5000 characters
                                </span>
                              </div>
                            </FormItem>
                          )}
                        />

                        <div className="flex justify-end">
                          <Button 
                            type="submit" 
                            disabled={isLoading || !form.formState.isValid}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 px-8"
                          >
                            {isLoading ? (
                              <>
                                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <MessageSquare className="mr-2 h-4 w-4" />
                                Submit Feedback
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}